import { useState, useMemo } from 'react'
import { apiError } from '../lib/errors'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, X, Trash2, FolderTree, Receipt, DollarSign } from 'lucide-react'
import { expensesApi, EXPENSE_CATEGORIES, COST_CENTER_TYPES, type CostCenter, type Expense } from '../lib/api'

const C = {
  bg: 'var(--color-bg)', card: 'var(--color-bg-card)', card2: 'var(--color-bg-card-2)',
  border: 'var(--color-border)', amber: 'var(--color-primary)', green: '#4CAF50',
  text: 'var(--color-text)', muted: 'var(--color-text-muted)', faint: 'var(--color-text-faint)',
  red: '#F87171',
}
const inp: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', background: C.card2, border: `1px solid ${C.border}`,
  borderRadius: 8, padding: '8px 12px', color: C.text, fontSize: 13, outline: 'none',
}
const lbl: React.CSSProperties = { display: 'block', fontSize: 11, fontWeight: 600, color: C.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }
const money = (n: number) => `$${(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

// Flatten the cost-center tree depth-first, tracking indent depth.
function flatten(nodes: CostCenter[] | undefined, depth = 0, out: { cc: CostCenter; depth: number }[] = []) {
  for (const cc of nodes ?? []) {
    out.push({ cc, depth })
    if (cc.children?.length) flatten(cc.children, depth + 1, out)
  }
  return out
}

function AddCostCenterModal({ flat, onClose }: { flat: { cc: CostCenter; depth: number }[]; onClose: () => void }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({ name: '', code: '', type: 'category', parent_id: '', description: '' })
  const [error, setError] = useState<string | null>(null)
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }))
  const save = useMutation({
    mutationFn: () => expensesApi.createCostCenter({
      name: form.name, code: form.code, type: form.type,
      parent_id: form.parent_id || null, description: form.description || undefined,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['cost-centers'] }); onClose() },
    onError: (e) => setError(apiError(e, 'Failed to save')),
  })
  return (
    <Modal title="Add cost center" onClose={onClose}>
      <form onSubmit={e => { e.preventDefault(); if (!form.name || !form.code) { setError('Name and code are required'); return } save.mutate() }} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
          <div><label style={lbl}>Name *</label><input style={inp} value={form.name} onChange={e => set('name', e.target.value)} placeholder="North Grove" /></div>
          <div><label style={lbl}>Code *</label><input style={inp} value={form.code} onChange={e => set('code', e.target.value)} placeholder="N-GRV" /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div><label style={lbl}>Type</label><select style={{ ...inp, cursor: 'pointer' }} value={form.type} onChange={e => set('type', e.target.value)}>{COST_CENTER_TYPES.map(t => <option key={t} value={t}>{cap(t)}</option>)}</select></div>
          <div><label style={lbl}>Parent</label><select style={{ ...inp, cursor: 'pointer' }} value={form.parent_id} onChange={e => set('parent_id', e.target.value)}><option value="">— none (top level) —</option>{flat.map(({ cc, depth }) => <option key={cc.id} value={cc.id}>{' '.repeat(depth * 2)}{cc.name}</option>)}</select></div>
        </div>
        <div><label style={lbl}>Description</label><input style={inp} value={form.description} onChange={e => set('description', e.target.value)} /></div>
        {error && <div style={{ fontSize: 12, color: C.red }}>{error}</div>}
        <button type="submit" disabled={save.isPending} style={btn(true)}>{save.isPending ? 'Saving…' : 'Add cost center'}</button>
      </form>
    </Modal>
  )
}

function LogExpenseModal({ flat, defaultCC, onClose }: { flat: { cc: CostCenter; depth: number }[]; defaultCC?: string; onClose: () => void }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({
    cost_center_id: defaultCC ?? flat[0]?.cc.id ?? '', amount: '', category: 'input',
    date: new Date().toISOString().slice(0, 10), vendor_name: '', description: '',
  })
  const [error, setError] = useState<string | null>(null)
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }))
  const save = useMutation({
    mutationFn: () => expensesApi.log({
      cost_center_id: form.cost_center_id, amount: parseFloat(form.amount), category: form.category,
      date: form.date, vendor_name: form.vendor_name || undefined, description: form.description || undefined,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['expenses'] }); qc.invalidateQueries({ queryKey: ['expense-pnl'] }); onClose() },
    onError: (e) => setError(apiError(e, 'Failed to save')),
  })
  return (
    <Modal title="Log expense" onClose={onClose}>
      <form onSubmit={e => { e.preventDefault(); if (!form.cost_center_id) { setError('Pick a cost center'); return } if (!form.amount || parseFloat(form.amount) <= 0) { setError('Enter an amount'); return } save.mutate() }} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div><label style={lbl}>Cost center *</label><select style={{ ...inp, cursor: 'pointer' }} value={form.cost_center_id} onChange={e => set('cost_center_id', e.target.value)}>{flat.length === 0 && <option value="">Add a cost center first</option>}{flat.map(({ cc, depth }) => <option key={cc.id} value={cc.id}>{' '.repeat(depth * 2)}{cc.name}</option>)}</select></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div><label style={lbl}>Amount ($) *</label><input style={inp} type="number" min={0} step={0.01} value={form.amount} onChange={e => set('amount', e.target.value)} placeholder="0.00" /></div>
          <div><label style={lbl}>Category</label><select style={{ ...inp, cursor: 'pointer' }} value={form.category} onChange={e => set('category', e.target.value)}>{EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{cap(c)}</option>)}</select></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div><label style={lbl}>Date</label><input style={inp} type="date" value={form.date} onChange={e => set('date', e.target.value)} /></div>
          <div><label style={lbl}>Vendor</label><input style={inp} value={form.vendor_name} onChange={e => set('vendor_name', e.target.value)} placeholder="Supplier" /></div>
        </div>
        <div><label style={lbl}>Description</label><input style={inp} value={form.description} onChange={e => set('description', e.target.value)} placeholder="What was it for?" /></div>
        {error && <div style={{ fontSize: 12, color: C.red }}>{error}</div>}
        <button type="submit" disabled={save.isPending} style={btn(true)}>{save.isPending ? 'Saving…' : 'Log expense'}</button>
      </form>
    </Modal>
  )
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, width: 460, maxWidth: '95vw', padding: 22, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.faint }}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  )
}
const btn = (primary: boolean): React.CSSProperties => ({ padding: '9px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: primary ? C.amber : C.card2, color: primary ? C.bg : C.muted })

export function ExpensesPage() {
  const qc = useQueryClient()
  const [showCC, setShowCC] = useState(false)
  const [showLog, setShowLog] = useState(false)
  const [selectedCC, setSelectedCC] = useState<string>('')

  const { data: ccData } = useQuery({ queryKey: ['cost-centers'], queryFn: () => expensesApi.costCenters().then(r => r.data) })
  const flat = useMemo(() => flatten(ccData?.cost_centers), [ccData])
  const ccName = (id: string) => flat.find(f => f.cc.id === id)?.cc.name ?? '—'

  const { data: expData, isLoading } = useQuery({
    queryKey: ['expenses', selectedCC],
    queryFn: () => expensesApi.list(selectedCC ? { cost_center_id: selectedCC } : undefined).then(r => r.data),
  })
  const { data: pnl } = useQuery({ queryKey: ['expense-pnl'], queryFn: () => expensesApi.farmProfitability().then(r => r.data) })
  const expenses = expData?.expenses ?? []
  const remove = useMutation({
    mutationFn: (id: string) => expensesApi.remove(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['expenses'] }); qc.invalidateQueries({ queryKey: ['expense-pnl'] }) },
  })

  return (
    <main id="main-content" style={{ padding: '28px 32px', minHeight: '100%', background: C.bg, color: C.text, fontFamily: '"DM Sans", system-ui, sans-serif' }}>
      {showCC && <AddCostCenterModal flat={flat} onClose={() => setShowCC(false)} />}
      {showLog && <LogExpenseModal flat={flat} defaultCC={selectedCC} onClose={() => setShowLog(false)} />}

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 22 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, fontFamily: '"Syne", system-ui, sans-serif' }}>Expenses</h1>
          <p style={{ margin: '3px 0 0', fontSize: 13, color: C.muted }}>Cost centers, spend log, and P&amp;L by crop / zone</p>
        </div>
        <button onClick={() => setShowLog(true)} style={{ ...btn(true), display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px' }}><Plus size={16} /> Log Expense</button>
      </div>

      {/* P&L summary */}
      {pnl && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginBottom: 22 }}>
          {[
            { label: 'Revenue', value: money(pnl.revenue), color: C.green },
            { label: 'Expenses', value: money(pnl.expenses), color: C.red },
            { label: 'Gross profit', value: money(pnl.gross_profit), color: pnl.gross_profit >= 0 ? C.green : C.red },
            { label: 'Margin', value: `${(pnl.margin_pct ?? 0).toFixed(1)}%`, color: C.muted },
          ].map(s => (
            <div key={s.label} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ fontSize: 11, color: C.faint, marginBottom: 4 }}>{s.label}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: s.color }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
        {/* Cost centers */}
        <div style={{ width: 250, flexShrink: 0, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '14px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: C.faint, textTransform: 'uppercase', letterSpacing: '0.06em' }}><FolderTree size={13} /> Cost centers</span>
            <button onClick={() => setShowCC(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.amber }}><Plus size={15} /></button>
          </div>
          <button onClick={() => setSelectedCC('')} style={{ width: '100%', textAlign: 'left', padding: '6px 8px', borderRadius: 6, border: 'none', cursor: 'pointer', background: selectedCC === '' ? 'color-mix(in srgb, var(--color-primary) 12%, transparent)' : 'transparent', color: selectedCC === '' ? C.amber : C.text, fontSize: 13, fontWeight: 600, marginBottom: 2 }}>All expenses</button>
          {flat.length === 0 ? (
            <div style={{ fontSize: 12, color: C.faint, padding: '8px 4px' }}>No cost centers yet. Add one to organize spend.</div>
          ) : flat.map(({ cc, depth }) => (
            <button key={cc.id} onClick={() => setSelectedCC(cc.id)} title={cc.path}
              style={{ width: '100%', textAlign: 'left', padding: '6px 8px', paddingLeft: 8 + depth * 14, borderRadius: 6, border: 'none', cursor: 'pointer', background: selectedCC === cc.id ? 'color-mix(in srgb, var(--color-primary) 12%, transparent)' : 'transparent', color: selectedCC === cc.id ? C.amber : C.text, fontSize: 13, display: 'flex', justifyContent: 'space-between', gap: 6 }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cc.name}</span>
              <span style={{ fontSize: 10, color: C.faint, flexShrink: 0 }}>{cc.type}</span>
            </button>
          ))}
        </div>

        {/* Expenses table */}
        <div style={{ flex: 1, minWidth: 0, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Receipt size={14} style={{ color: C.amber }} />
            <span style={{ fontSize: 13, fontWeight: 700 }}>{selectedCC ? ccName(selectedCC) : 'All'} expenses</span>
            <span style={{ fontSize: 11, color: C.faint }}>{expenses.length}</span>
          </div>
          {isLoading ? (
            <div style={{ padding: '40px 0', textAlign: 'center', color: C.faint }}>Loading…</div>
          ) : expenses.length === 0 ? (
            <div style={{ padding: '48px 24px', textAlign: 'center', color: C.faint }}>
              <DollarSign size={32} style={{ opacity: 0.4, marginBottom: 8 }} />
              <p style={{ margin: 0 }}>No expenses logged{selectedCC ? ' for this cost center' : ''}.</p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ color: C.faint, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <th style={{ textAlign: 'left', padding: '8px 16px' }}>Date</th>
                  <th style={{ textAlign: 'left', padding: '8px 8px' }}>Category</th>
                  <th style={{ textAlign: 'left', padding: '8px 8px' }}>Description</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px' }}>Amount</th>
                  <th style={{ padding: '8px 16px' }} />
                </tr>
              </thead>
              <tbody>
                {expenses.map((e: Expense) => (
                  <tr key={e.id} style={{ borderTop: `1px solid ${C.border}` }}>
                    <td style={{ padding: '9px 16px', color: C.muted }}>{new Date(e.date).toLocaleDateString([], { month: 'short', day: 'numeric', year: '2-digit' })}</td>
                    <td style={{ padding: '9px 8px' }}><span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, background: 'color-mix(in srgb, var(--color-primary) 12%, transparent)', color: C.amber }}>{cap(e.category)}</span></td>
                    <td style={{ padding: '9px 8px', color: C.text, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.description || e.vendor_name || '—'}{e.vendor_name && e.description ? <span style={{ color: C.faint }}> · {e.vendor_name}</span> : ''}</td>
                    <td style={{ padding: '9px 8px', textAlign: 'right', fontWeight: 600 }}>{money(e.amount)}</td>
                    <td style={{ padding: '9px 16px', textAlign: 'right' }}>
                      <button onClick={() => { if (confirm('Delete this expense?')) remove.mutate(e.id) }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.faint }} aria-label="Delete expense"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </main>
  )
}

export default ExpensesPage
